import { AudienceDeleteDialog, BaseDialog, Button, CheckSelectField, HookedForm, InputField, Loading } from '@app/components';
import { TagsTable } from '@app/components/TagsTable/TagsTable';
import { usePermissions } from '@app/contexts/PermissionContext.tsx';
import { CustomerInterface } from '@app/models/business';
import { deleteManyTags, getTagsDeleteImpact } from '@app/services/tag';
import AddIcon from '@app/assets/icons/add.svg?react';
import CloseIcon from '@app/assets/icons/close.svg?react';
import TagIcon from '@app/assets/icons/tag.svg?react';
import FolderIllustration from '@app/assets/illustrations/folder.svg?react';
import { useTagsPage } from './Tags.hooks';

export const Tags = () => {
  const {
    addTagOpen,
    handleAddTag,
    createTagForm,
    submitTag,
    isPendingTag,
    tagsData,
    isLoading,
    customers,
    tagQuery,
    selectedTags,
    handleSelectedTags,
    handleNextPage,
    handlePrevPage,
    handleLimit,
    sortBy,
    sortOrder,
    handleSortingTable,
    deleteIds,
    setDeleteIds,
    deletingTags,
    handleDeleted,
  } = useTagsPage();
  const { can } = usePermissions();

  const allCustomers =
    customers?.data?.customers?.map((customer: CustomerInterface) => ({
      label: `${customer.firstName} ${customer.lastName}`,
      value: customer.id,
    })) ?? [];

  return (
    <>
      <div>
        <div className="bg-white border-b border-b-base-ghostWhite ">
          <div className="container mx-auto p-4 flex justify-between items-center flex-col md:flex-row">
            <h2 className="font-bold text-[28px]">Tags</h2>
            {can('editLists') && (
              <Button onClick={handleAddTag} leftNode={<AddIcon />} className="text-sm font-semibold">
                Create new tag
              </Button>
            )}
          </div>
        </div>
        <div className="container mx-auto p-4">
          {isLoading && <Loading />}
          {Boolean(!isLoading && tagsData?.tags?.length) && (
            <TagsTable
              tags={tagsData?.tags ?? []}
              total={tagsData?.count ?? 0}
              page={tagQuery.page}
              handleNextPage={handleNextPage}
              handlePrevPage={handlePrevPage}
              handleLimit={handleLimit}
              selectedTags={selectedTags}
              handleTagSelect={handleSelectedTags}
              handleDeleteSelected={() => setDeleteIds(selectedTags)}
              handleDeleteTag={(id) => setDeleteIds([id])}
              canEdit={can('editLists')}
              currentSortBy={sortBy}
              currentSortOrder={sortOrder}
              onSort={handleSortingTable}
            />
          )}
          {!isLoading && tagsData?.count === 0 && (
            <div className="flex items-center justify-center flex-col max-w-[350px] mx-auto space-y-4 h-full mt-20">
              <FolderIllustration />
              <p className="font-bold">No tags</p>
              <p className="text-sm text-base-coolGray text-center">Label your customers with tags to filter them and target automations</p>
              {can('editLists') && (
                <Button leftNode={<AddIcon />} onClick={handleAddTag}>
                  Create new tag
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      <BaseDialog size="max-w-2xl" open={addTagOpen} onClose={handleAddTag}>
        <div className="border-b border-b-base-ghostWhite p-6 mb-6 flex gap-2 items-start justify-between">
          <div className="bg-base-antiFlash flex items-center p-2 rounded-lg">
            <TagIcon width={24} height={24} />
          </div>
          <div className="mr-auto">
            <h5 className="font-bold text-lg">Create new tag</h5>
            <p className="text-base-coolGray text-sm">Fill in the fields below and create a new tag</p>
          </div>
          <div>
            <button type="button" onClick={handleAddTag}>
              <CloseIcon />
            </button>
          </div>
        </div>
        <div className="px-6 pb-4">
          <HookedForm formHook={createTagForm} onSubmit={submitTag} className="space-y-4">
            <InputField name="name" label="Name" />
            <CheckSelectField showPill name="customers" label="Add customer(s)" isOptional options={allCustomers} />
            <div className="flex items-center gap-4 justify-center ">
              <Button kind="secondary" className="w-full" type="button" onClick={handleAddTag}>
                Cancel
              </Button>
              <Button disabled={isPendingTag} loading={isPendingTag} className="w-full" type="submit">
                Create tag
              </Button>
            </div>
          </HookedForm>
        </div>
      </BaseDialog>

      <AudienceDeleteDialog
        open={Boolean(deleteIds)}
        onClose={() => setDeleteIds(null)}
        noun="tag"
        ids={deleteIds ?? []}
        names={deletingTags.map((tag) => tag.name)}
        customersCount={deletingTags.reduce((sum, tag) => sum + (tag.customersCount ?? 0), 0)}
        getImpact={getTagsDeleteImpact}
        deleteItems={deleteManyTags}
        onDeleted={handleDeleted}
      />
    </>
  );
};
